import type {
  ApiClient,
  ArtifactDetail,
  ArtifactRecord,
  AttachmentInspection,
  AttachmentInspectionStatus,
  AttachmentRef,
  AuthSession,
  BootstrapData,
  CreateProjectResult,
  EvidenceOrigin,
  EvidenceRecord,
  EvidenceStatus,
  ComposerCapability,
  JsonRecord,
  LocalFolderSelection,
  MeResponse,
  OrganizationAgentObservation,
  OrganizationBTWResponse,
  OrganizationObservation,
  OrganizationStatus,
  PermissionRequest,
  ProjectTurn,
  ProjectTurnIntent,
  ProjectTurnPage,
  ProjectGitStatus,
  ProjectSummary,
  ReportTextReplacementResult,
  ResearchStateSection,
  ResearchStateView,
  SessionReplayGap,
  SessionSnapshot,
  SessionSummary,
  TurnRecord,
  TurnStartResult,
  TurnStatus,
  UploadDocumentSelectionLocator,
  UploadDocumentRepresentation,
  WorkspaceEntry,
  WorkspaceListing,
} from "./types";

const DEFAULT_API_BASE = "";
const unauthorizedListeners = new Set<() => void>();

export function subscribeUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

export function notifyUnauthorized(): void {
  unauthorizedListeners.forEach((listener) => listener());
}

export class ApiError extends Error {
  readonly status: number;
  readonly body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export class UploadFilesError extends Error {
  readonly uploaded: AttachmentRef[];
  readonly failedFiles: File[];
  readonly cause: unknown;

  constructor(
    message: string,
    uploaded: AttachmentRef[],
    failedFiles: File[],
    cause: unknown,
  ) {
    super(message);
    this.name = "UploadFilesError";
    this.uploaded = uploaded;
    this.failedFiles = failedFiles;
    this.cause = cause;
  }
}

export function createHttpApiClient(baseUrl = DEFAULT_API_BASE): ApiClient {
  const normalizedBase = baseUrl.replace(/\/$/, "");
  const apiPath = (path: string) => `${normalizedBase}${path}`;

  return {
    streamSupported: true,

    async getAuthSession() {
      const raw = await requestJson<unknown>(apiPath("/api/auth/session"));
      const session = normalizeAuthSession(raw);
      if (!session.authenticated) notifyUnauthorized();
      return session;
    },

    async login(input) {
      const raw = await requestJson<unknown>(apiPath("/api/auth/login"), {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify(input),
      });
      return normalizeAuthSession(raw);
    },

    async logout() {
      await requestJson<unknown>(apiPath("/api/auth/logout"), {
        method: "POST",
      });
      notifyUnauthorized();
    },

    async bootstrap() {
      return requestJson<BootstrapData>(apiPath("/api/bootstrap"));
    },

    async getMe() {
      const raw = await requestJson<unknown>(apiPath("/api/me"));
      return normalizeMe(raw);
    },

    async listProjects() {
      const raw = await requestJson<unknown>(apiPath("/api/projects"));
      const record = recordFrom(raw);
      return collectionFrom(record.projects, ["projects"]).map((item, index) =>
        normalizeProject(item, `project-${index + 1}`),
      );
    },

    async createProject(input) {
      const raw = await requestJson<unknown>(apiPath("/api/projects"), {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify(input),
      });
      const record = recordFrom(raw);
      const project = normalizeProject(
        record.project ?? record,
        "created-project",
      );
      const snapshotRaw = record.snapshot;
      if (snapshotRaw !== undefined && (!snapshotRaw || typeof snapshotRaw !== "object")) {
        throw contractError("新建项目响应缺少 snapshot。", raw);
      }
      return {
        project,
        snapshot:
          snapshotRaw && typeof snapshotRaw === "object"
            ? normalizeSnapshot(snapshotRaw, project.sessionId)
            : undefined,
        raw: record,
      } satisfies CreateProjectResult;
    },

    async selectLocalFolder() {
      const raw = await requestJson<unknown>(apiPath("/api/local-folders/select"), {
        method: "POST",
        headers: jsonHeaders,
      });
      const record = recordFrom(raw);
      if (record.selected === false) return undefined;
      const selectionId = stringFrom(record.selection_id);
      const name = stringFrom(record.name) ?? "导入文件夹";
      if (!selectionId) {
        throw contractError("本地文件夹选择响应缺少 selection_id。", raw);
      }
      return {
        selectionId,
        name,
        expiresAt: numberFrom(record.expires_at),
      } satisfies LocalFolderSelection;
    },

    async importProject(input) {
      const raw = await requestJson<unknown>(apiPath("/api/projects/import"), {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({
          selection_id: input.selectionId,
          mode: input.mode,
          ...(input.name?.trim() ? { name: input.name.trim() } : {}),
        }),
      });
      const record = recordFrom(raw);
      return {
        project: normalizeProject(record.project ?? record, "imported-project"),
        raw: record,
      } satisfies CreateProjectResult;
    },

    async getProjectGitStatus(projectId) {
      const raw = await requestJson<unknown>(
        apiPath(`/api/projects/${encodeURIComponent(projectId)}/git/branches`),
      );
      return normalizeProjectGitStatus(raw);
    },

    async switchProjectGitBranch(input) {
      const raw = await requestJson<unknown>(
        apiPath(`/api/projects/${encodeURIComponent(input.projectId)}/git/switch`),
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({
            branch: input.branch,
            expected_head_oid: input.expectedHeadOid,
          }),
        },
      );
      return normalizeProjectGitStatus(raw);
    },

    async listSessions() {
      const raw = await requestJson<unknown>(apiPath("/api/sessions"));
      return collectionFrom(raw, ["sessions", "items", "data"]).map(
        (item, index) => normalizeSession(item, `session-${index + 1}`),
      );
    },

    async createSession(input) {
      const raw = await requestJson<unknown>(apiPath("/api/sessions"), {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify(input),
      });
      const record = recordFrom(raw);
      const snapshot = recordFrom(record.snapshot);
      const metadata = recordFrom(snapshot.metadata);
      const session = {
        ...snapshot,
        ...recordFrom(record.session ?? record.data),
        id: record.session_id ?? snapshot.session_id,
        session_id: record.session_id ?? snapshot.session_id,
        title: metadata.title ?? "新会话",
        running: true,
        resumable: false,
      };
      return normalizeSession(session, `session-${Date.now()}`);
    },

    async getSnapshot(sessionId) {
      const raw = await requestJson<unknown>(
        apiPath(`/api/sessions/${encodeURIComponent(sessionId)}/snapshot`),
      );
      return normalizeSnapshot(raw, sessionId);
    },

    async listProjectTurns(projectId, cursor) {
      const query = new URLSearchParams({ limit: "50" });
      if (cursor) query.set("cursor", cursor);
      const raw = await requestJson<unknown>(
        apiPath(
          `/api/projects/${encodeURIComponent(projectId)}/turns?${query.toString()}`,
        ),
      );
      return normalizeProjectTurnPage(raw);
    },

    async getComposerCapabilities(projectId) {
      const raw = await requestJson<unknown>(
        apiPath(
          `/api/projects/${encodeURIComponent(projectId)}/composer-capabilities`,
        ),
      );
      return normalizeComposerCapabilities(raw);
    },

    async sendTurn(sessionId, input) {
      const attachments = serializeAttachments(input.attachments);
      return requestJson<TurnStartResult>(
        apiPath(`/api/sessions/${encodeURIComponent(sessionId)}/turns`),
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({ ...input, attachments }),
        },
      );
    },

    async cancelTurn(sessionId, turnId) {
      await requestJson<unknown>(
        apiPath(`/api/sessions/${encodeURIComponent(sessionId)}/cancel`),
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({ turn_id: turnId }),
        },
      );
    },

    async listFiles(sessionId, path) {
      const query = new URLSearchParams();
      if (path) query.set("path", path);
      const suffix = query.size ? `?${query.toString()}` : "";
      const raw = await requestJson<unknown>(
        apiPath(
          `/api/sessions/${encodeURIComponent(sessionId)}/files${suffix}`,
        ),
      );
      return normalizeWorkspaceListing(raw, path);
    },

    async previewFile(sessionId, path) {
      const query = new URLSearchParams({ path });
      return requestArtifact(
        apiPath(
          `/api/sessions/${encodeURIComponent(sessionId)}/files/preview?${query.toString()}`,
        ),
        path.split("/").filter(Boolean).at(-1) ?? path,
      );
    },

    async listUploads(sessionId) {
      const raw = await requestJson<unknown>(
        apiPath(`/api/sessions/${encodeURIComponent(sessionId)}/uploads`),
      );
      return collectionFrom(raw, ["uploads", "items", "data"]).map((item) => {
        const attachment = normalizeAttachment(item, "upload");
        validateUploadScope(attachment, sessionId, item);
        return attachment;
      });
    },

    async uploadFiles(sessionId, files) {
      const attachments: AttachmentRef[] = [];
      for (const [index, file] of files.entries()) {
        try {
          const body = new FormData();
          body.append("file", file, file.name);
          const raw = await requestJson<unknown>(
            apiPath(`/api/sessions/${encodeURIComponent(sessionId)}/uploads`),
            { method: "POST", body },
          );
          const attachment = normalizeAttachment(raw, "upload", file.name);
          validateUploadScope(attachment, sessionId, raw);
          attachments.push(attachment);
        } catch (cause) {
          /*
           * Uploads are durable one-by-one. Preserve the successful server
           * records and expose only the failed suffix for retry; retrying the
           * whole File[] would duplicate earlier Project Inputs.
           */
          throw new UploadFilesError(
            cause instanceof Error && cause.message
              ? cause.message
              : "文件上传失败。",
            attachments,
            files.slice(index),
            cause,
          );
        }
      }
      return attachments;
    },

    async prepareUpload(sessionId, uploadId) {
      const raw = await requestJson<unknown>(
        apiPath(
          `/api/sessions/${encodeURIComponent(sessionId)}/uploads/${encodeURIComponent(uploadId)}/prepare`,
        ),
        { method: "POST" },
      );
      const attachment = normalizeAttachment(raw, "upload");
      validateUploadScope(attachment, sessionId, raw);
      return attachment;
    },

    async getUploadDocument(sessionId, uploadId) {
      const raw = await requestJson<unknown>(
        apiPath(
          `/api/sessions/${encodeURIComponent(sessionId)}/uploads/${encodeURIComponent(uploadId)}/document`,
        ),
      );
      return normalizeUploadDocumentRepresentation(raw, uploadId);
    },

    uploadSourceUrl(sessionId, uploadId) {
      return apiPath(
        `/api/sessions/${encodeURIComponent(sessionId)}/uploads/${encodeURIComponent(uploadId)}/source`,
      );
    },

    async listArtifacts(sessionId) {
      const raw = await requestJson<unknown>(
        apiPath(`/api/sessions/${encodeURIComponent(sessionId)}/artifacts`),
      );
      return collectionFrom(raw, ["artifacts", "items", "data"])
        .map((item, index) => normalizeArtifact(item, `artifact-${index + 1}`))
        .filter(isListedArtifact);
    },

    async getArtifact(sessionId, artifactId) {
      return requestArtifact(
        apiPath(
          `/api/sessions/${encodeURIComponent(sessionId)}/artifacts/${encodeURIComponent(artifactId)}`,
        ),
        artifactId,
        { directFile: true },
      );
    },

    async updateArtifact(sessionId, artifactId, input) {
      const raw = await requestJson<unknown>(
        apiPath(
          `/api/sessions/${encodeURIComponent(sessionId)}/artifacts/${encodeURIComponent(artifactId)}`,
        ),
        {
          method: "PUT",
          headers: jsonHeaders,
          body: JSON.stringify(input),
        },
      );
      return recordFrom(raw);
    },

    async applyReportTextReplacement(sessionId, artifactId, input) {
      const raw = await requestJson<unknown>(
        apiPath(
          `/api/sessions/${encodeURIComponent(sessionId)}/artifacts/${encodeURIComponent(artifactId)}/text-replacements`,
        ),
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify(input),
        },
      );
      const record = recordFrom(raw);
      const content = typeof record.content === "string" ? record.content : undefined;
      const contentSha256 =
        typeof record.content_sha256 === "string"
          ? record.content_sha256
          : undefined;
      if (!content || !contentSha256 || record.version !== 1) {
        throw new ApiError(
          "文本 Artifact 替换响应缺少 version/content/content_sha256。",
          502,
          raw,
        );
      }
      return {
        version: 1,
        artifactId:
          typeof record.artifact_id === "string"
            ? record.artifact_id
            : artifactId,
        contentSha256,
        content,
        raw: record,
      } satisfies ReportTextReplacementResult;
    },

    artifactUrl(sessionId, artifactId) {
      return apiPath(
        `/api/sessions/${encodeURIComponent(sessionId)}/artifacts/${encodeURIComponent(artifactId)}`,
      );
    },

    async getOrganizationObservation(projectId) {
      const query = new URLSearchParams({ project_id: projectId });
      const raw = await requestJson<unknown>(
        apiPath(`/api/organization?${query.toString()}`),
      );
      return normalizeOrganizationObservation(raw);
    },

    async getOrganizationAgentStatus(projectId, agentId, maxSummaries = 10) {
      const query = new URLSearchParams({
        project_id: projectId,
        max_summaries: String(Math.min(10, Math.max(0, maxSummaries))),
      });
      const raw = await requestJson<unknown>(
        apiPath(
          `/api/organization/agents/${encodeURIComponent(agentId)}/status?${query.toString()}`,
        ),
      );
      return normalizeOrganizationStatus(raw, agentId);
    },

    async sendOrganizationRoot(input) {
      const attachments = serializeAttachments(input.attachments);
      return requestJson<JsonRecord>(apiPath("/api/organization/messages"), {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({ ...input, attachments }),
      });
    },

    async sendOrganizationMessage(agentId, input) {
      return requestJson<JsonRecord>(
        apiPath(
          `/api/organization/agents/${encodeURIComponent(agentId)}/messages`,
        ),
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify(input),
        },
      );
    },

    async steerOrganizationProjectTurn(projectId, parentRequestId, input) {
      return requestJson<JsonRecord>(
        apiPath(
          `/api/projects/${encodeURIComponent(projectId)}/turns/${encodeURIComponent(parentRequestId)}/steer`,
        ),
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify(input),
        },
      );
    },

    async sendOrganizationBTW(agentId, input) {
      const raw = await requestJson<unknown>(
        apiPath(`/api/organization/agents/${encodeURIComponent(agentId)}/btw`),
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify(input),
        },
      );
      return normalizeOrganizationBTWResponse(raw);
    },

    organizationAgentEventUrl(projectId, agentId) {
      const query = new URLSearchParams({ project_id: projectId });
      return apiPath(
        `/api/organization/agents/${encodeURIComponent(agentId)}/events?${query.toString()}`,
      );
    },

    async getProjectChildThreadTimeline(projectId, threadId, options) {
      const query = new URLSearchParams();
      if (options?.after !== undefined) query.set("after", String(options.after));
      if (options?.limit !== undefined) query.set("limit", String(options.limit));
      const suffix = query.size ? `?${query.toString()}` : "";
      return requestJson<JsonRecord>(
        apiPath(
          `/api/projects/${encodeURIComponent(projectId)}/child-threads/${encodeURIComponent(threadId)}/timeline${suffix}`,
        ),
      );
    },

    async getProjectResearchState(projectId, section, options) {
      const query = new URLSearchParams({ section });
      if (options?.query) query.set("query", options.query);
      query.set("limit", String(options?.limit ?? 50));
      return requestJson<ResearchStateView>(
        apiPath(
          `/api/projects/${encodeURIComponent(projectId)}/research-state?${query.toString()}`,
        ),
      );
    },

    async decidePermission(sessionId, requestId, decision) {
      await requestJson<unknown>(
        apiPath(
          `/api/sessions/${encodeURIComponent(sessionId)}/permissions/${encodeURIComponent(requestId)}`,
        ),
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({ decision }),
        },
      );
    },

    eventUrl(sessionId, sinceSeq) {
      const query = new URLSearchParams({ since_seq: String(sinceSeq) });
      return apiPath(
        `/api/sessions/${encodeURIComponent(sessionId)}/events?${query.toString()}`,
      );
    },
  };
}

const jsonHeaders = { "Content-Type": "application/json" };
const SCIENTIFIC_VIEW_DATA_MAX_ROWS = 250;
const SCIENTIFIC_VIEW_DATA_MAX_BYTES = 256 * 1024;

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { ...init, credentials: "same-origin" });
  const text = await response.text();
  let body: unknown;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      if (response.ok) {
        throw new ApiError(
          "研究工作台服务返回了无法识别的响应。请确认 Cadence API 服务可访问后重试。",
          response.status,
          text,
        );
      }
      body = text;
    }
  }
  if (!response.ok) {
    if (response.status === 401) notifyUnauthorized();
    const record = recordFrom(body);
    const errorRecord = recordFrom(record.error);
    const message =
      stringFrom(
        record.message ??
          errorRecord.message ??
          record.error ??
          record.detail,
      ) ??
      `请求失败（HTTP ${response.status}）`;
    throw new ApiError(message, response.status, body);
  }
  return body as T;
}

async function requestArtifact(
  url: string,
  fallbackName: string,
  options?: { directFile?: boolean },
): Promise<ArtifactDetail> {
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) {
    if (response.status === 401) notifyUnauthorized();
    const text = await response.text();
    let body: unknown = text;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        // Plain-text artifact endpoints remain supported.
      }
    }
    const record = recordFrom(body);
    const errorRecord = recordFrom(record.error);
    const message =
      stringFrom(
        record.message ??
          errorRecord.message ??
          record.error ??
          record.detail,
      ) ?? `无法读取产物详情（HTTP ${response.status}）`;
    throw new ApiError(
      message,
      response.status,
      body,
    );
  }
  const contentType = response.headers.get("content-type") ?? undefined;
  const disposition = response.headers.get("content-disposition") ?? "";
  const responseName = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(
    disposition,
  )?.[1];
  const name = responseName ? decodeURIComponent(responseName) : fallbackName;

  if (options?.directFile) {
    const blob = await response.blob();
    const content = isTextualArtifactContentType(contentType)
      ? await blob.text()
      : undefined;
    const json = content && isJsonContentType(contentType)
      ? jsonFromText(content)
      : undefined;
    const legacyEnvelope = json
      ? legacyArtifactEnvelope(json, fallbackName, name, contentType)
      : undefined;
    if (legacyEnvelope) return legacyEnvelope;

    /*
     * Artifact GET now returns the direct file body, including JSON. The
     * Project Artifact list remains the metadata authority; retaining the
     * Blob and raw text here avoids manufacturing a client-side envelope.
     */
    return {
      id: fallbackName,
      name,
      content,
      mimeType: contentType,
      blob,
      raw: recordFrom(json),
    };
  }

  if (contentType?.includes("application/json")) {
    const raw = await response.json();
    const record = recordFrom(raw);
    const content =
      contentFrom(
        record.content ?? record.preview ?? record.markdown ?? record.text,
      ) ?? scientificViewDataContentFromRecord(record);
    return {
      id: stringFrom(record.id ?? record.artifact_id) ?? fallbackName,
      name: stringFrom(record.name ?? record.filename ?? record.path) ?? name,
      content,
      mimeType:
        stringFrom(record.mime_type ?? record.content_type) ?? contentType,
      previewUrl: safeResponseUrl(stringFrom(record.preview_url)),
      downloadUrl: safeResponseUrl(stringFrom(record.download_url)),
      raw: record,
    };
  }

  if (contentType?.startsWith("text/")) {
    return {
      id: fallbackName,
      name,
      content: await response.text(),
      mimeType: contentType,
    };
  }

  return {
    id: fallbackName,
    name,
    mimeType: contentType,
    blob: await response.blob(),
  };
}

function isTextualArtifactContentType(contentType?: string): boolean {
  const mimeType = contentType?.split(";", 1)[0].trim().toLowerCase();
  return Boolean(
    mimeType?.startsWith("text/") ||
      mimeType === "application/json" ||
      mimeType?.endsWith("+json") ||
      mimeType === "application/javascript" ||
      mimeType === "application/x-javascript" ||
      mimeType === "application/xml" ||
      mimeType?.endsWith("+xml"),
  );
}

function isJsonContentType(contentType?: string): boolean {
  const mimeType = contentType?.split(";", 1)[0].trim().toLowerCase();
  return Boolean(
    mimeType === "application/json" || mimeType?.endsWith("+json"),
  );
}

function jsonFromText(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function legacyArtifactEnvelope(
  value: unknown,
  fallbackName: string,
  responseName: string,
  contentType?: string,
): ArtifactDetail | undefined {
  const record = recordFrom(value);
  const contentValue =
    record.content ?? record.preview ?? record.markdown ?? record.text;
  const envelopeId = stringFrom(record.id ?? record.artifact_id);
  const envelopeMimeType = stringFrom(record.mime_type ?? record.content_type);
  if (contentValue === undefined || !envelopeId || !envelopeMimeType) {
    return undefined;
  }
  return {
    id: envelopeId ?? fallbackName,
    name: stringFrom(record.name ?? record.filename ?? record.path) ?? responseName,
    content:
      contentFrom(contentValue) ?? scientificViewDataContentFromRecord(record),
    mimeType: envelopeMimeType ?? contentType,
    previewUrl: safeResponseUrl(stringFrom(record.preview_url)),
    downloadUrl: safeResponseUrl(stringFrom(record.download_url)),
    raw: record,
  };
}

function scientificViewDataContentFromRecord(
  record: JsonRecord,
): string | undefined {
  if (record.kind !== "scientific_view_data") return undefined;
  if (record.version !== undefined && record.version !== 1) return undefined;
  const sourceView = recordFrom(record.view);
  const rows = sourceView.rows;
  if (!Array.isArray(rows) || rows.length > SCIENTIFIC_VIEW_DATA_MAX_ROWS) {
    return undefined;
  }

  const recordRows = rows.flatMap((row) => {
    const item = recordFrom(row);
    if (
      typeof item.id !== "string" ||
      !isRecordValue(item.values) ||
      !isScientificViewValue(item.values)
    ) {
      return [];
    }
    return [{ id: item.id, values: item.values }];
  });
  const sourceColumns = Array.isArray(sourceView.columns)
    ? sourceView.columns
    : undefined;
  const columns = sourceColumns
    ? sourceColumns.filter(
        (column): column is string => typeof column === "string",
      )
    : undefined;
  const tableRows =
    sourceView.view_type === "table" &&
    columns &&
    sourceColumns &&
    columns.length === sourceColumns.length &&
    rows.flatMap((row) => {
      if (!Array.isArray(row)) return [];
      const values = columns.map((_, index) => row[index] ?? null);
      return values.every((value) => isScientificViewValue(value))
        ? [values]
        : [];
    });
  const normalizedRows =
    recordRows.length === rows.length
      ? recordRows
      : tableRows && tableRows.length === rows.length
        ? tableRows
        : undefined;
  if (!normalizedRows) return undefined;

  const view: JsonRecord = {
    rows: normalizedRows,
    truncated:
      typeof sourceView.truncated === "boolean" ? sourceView.truncated : false,
  };
  if (typeof sourceView.view_type === "string") {
    view.view_type = sourceView.view_type;
  }
  if (columns) view.columns = columns;

  const payload: JsonRecord = {
    version: 1,
    kind: "scientific_view_data",
    view,
  };
  for (const key of ["id", "source_ref"] as const) {
    const value = stringFrom(record[key]);
    if (value) payload[key] = value;
  }
  const content = JSON.stringify(payload);
  if (
    new TextEncoder().encode(content).byteLength >
    SCIENTIFIC_VIEW_DATA_MAX_BYTES
  ) {
    return undefined;
  }
  return content;
}

function isRecordValue(value: unknown): value is JsonRecord {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function isScientificViewValue(value: unknown, depth = 0): boolean {
  if (depth > 5) return false;
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return true;
  }
  if (typeof value === "number") return Number.isFinite(value);
  if (Array.isArray(value)) {
    return (
      value.length <= 100 &&
      value.every((item) => isScientificViewValue(item, depth + 1))
    );
  }
  if (!isRecordValue(value)) return false;
  const entries = Object.entries(value);
  return (
    entries.length <= 100 &&
    entries.every(([, item]) => isScientificViewValue(item, depth + 1))
  );
}

function normalizeAuthSession(raw: unknown): AuthSession {
  const record = recordFrom(raw);
  const user = recordFrom(record.user);
  const hasUser = Object.keys(user).length > 0;
  return {
    authenticated: record.authenticated === true,
    user: hasUser ? normalizeMe(user) : undefined,
    raw: record,
  };
}

export function normalizeSnapshot(
  raw: unknown,
  sessionId: string,
): SessionSnapshot {
  const record = recordFrom(raw);
  const turnCandidates = collectionFrom(record.turns ?? record.messages ?? [], [
    "turns",
    "messages",
    "items",
    "data",
  ]);
  const evidenceCandidates = collectionFrom(record.evidence ?? [], [
    "evidence",
    "items",
    "records",
    "data",
  ]);
  const artifactCandidates = collectionFrom(record.artifacts ?? [], [
    "artifacts",
    "items",
    "files",
    "data",
  ]);
  const permissionCandidates = collectionFrom(
    record.permissions ??
      record.permission_requests ??
      record.pending_permissions ??
      [],
    ["permissions", "requests", "pending", "items", "data"],
  );
  const sessionRecord = recordFrom(record.session);
  const session = Object.keys(sessionRecord).length
    ? normalizeSession(sessionRecord, sessionId)
    : undefined;
  const activeTurnId = stringFrom(
    record.active_turn_id ??
      record.running_turn_id ??
      sessionRecord.active_turn_id,
  );
  const externalNetworkUsed = booleanFrom(
    record.external_network_used ??
      record.network_used ??
      recordFrom(record.evidence_summary).external_network_used,
  );

  return {
    session,
    turns: normalizeTurns(turnCandidates, activeTurnId),
    evidence: evidenceCandidates.map((item, index) =>
      normalizeEvidence(item, `evidence-${index + 1}`),
    ),
    artifacts: artifactCandidates
      .map((item, index) => normalizeArtifact(item, `artifact-${index + 1}`))
      .filter(isListedArtifact),
    permissions: permissionCandidates.map((item, index) =>
      normalizePermission(item, `permission-${index + 1}`),
    ),
    externalNetworkUsed,
    warnings: stringList(record.warnings ?? record.warning),
    activeTurnId,
    lastSeq:
      numberFrom(record.last_seq ?? record.seq ?? record.latest_seq) ?? 0,
    raw: record,
  };
}

export function normalizeSessionReplayGap(
  raw: unknown,
  expectedSessionId: string,
): SessionReplayGap | undefined {
  const record = recordFrom(raw);
  if (stringFrom(record.type) !== "session.replay_gap") return undefined;
  if ("seq" in record || "event_id" in record) return undefined;
  const version = numberFrom(record.version);
  const sessionId = stringFrom(record.session_id);
  const reason = stringFrom(record.reason);
  const requestedAfterSeq = numberFrom(record.requested_after_seq);
  const latestSeq = numberFrom(record.latest_seq);
  const oldestAvailableSeq =
    record.oldest_available_seq === null
      ? null
      : numberFrom(record.oldest_available_seq);
  const broadcastSkipped =
    record.broadcast_skipped === null
      ? null
      : numberFrom(record.broadcast_skipped);
  const snapshotRecord = recordFrom(record.snapshot);
  if (
    version !== 1 ||
    sessionId !== expectedSessionId ||
    reason !== "cursor_outside_retained_history" ||
    requestedAfterSeq === undefined ||
    latestSeq === undefined ||
    oldestAvailableSeq === undefined ||
    broadcastSkipped === undefined ||
    record.reconnect_required !== true ||
    !Object.keys(snapshotRecord).length ||
    numberFrom(snapshotRecord.last_seq) === undefined
  ) {
    return undefined;
  }
  return {
    version: 1,
    type: "session.replay_gap",
    sessionId,
    reason: "cursor_outside_retained_history",
    requestedAfterSeq,
    oldestAvailableSeq,
    latestSeq,
    broadcastSkipped,
    reconnectRequired: true,
    snapshot: normalizeSnapshot(snapshotRecord, sessionId),
    raw: record,
  };
}

export function normalizeMe(raw: unknown): MeResponse {
  const record = recordFrom(raw);
  const userId = stringFrom(record.user_id);
  const displayName = stringFrom(record.display_name);
  const authMode = stringFrom(record.auth_mode);
  if (!userId || !displayName || !authMode) {
    throw contractError(
      "用户身份响应必须包含 user_id、display_name 和 auth_mode。",
      raw,
    );
  }
  return { userId, displayName, authMode, raw: record };
}

export function normalizeProjectTurnPage(raw: unknown): ProjectTurnPage {
  const record = recordFrom(raw);
  if (!Array.isArray(record.turns) || typeof record.has_more !== "boolean") {
    throw contractError(
      "项目请求历史响应必须包含 turns 数组和 has_more 布尔值。",
      raw,
    );
  }
  if (
    record.next_cursor !== null &&
    record.next_cursor !== undefined &&
    typeof record.next_cursor !== "string"
  ) {
    throw contractError("项目请求历史 next_cursor 必须是字符串或 null。", raw);
  }
  return {
    turns: record.turns.map((item, index) =>
      normalizeProjectTurn(item, `project-turn-${index + 1}`),
    ),
    nextCursor:
      typeof record.next_cursor === "string" ? record.next_cursor : null,
    hasMore: record.has_more,
  };
}

export function normalizeProjectTurn(
  raw: unknown,
  fallbackId: string,
): ProjectTurn {
  const record = recordFrom(raw);
  const id = stringFrom(record.id) ?? fallbackId;
  const requestId = stringFrom(record.request_id);
  const projectId = stringFrom(record.project_id);
  const kind = stringFrom(record.kind);
  const status = normalizeTurnStatus(record.status);
  if (!requestId || !projectId || !kind || !status) {
    throw contractError(
      `项目请求 ${id} 缺少 request_id/project_id/kind/status。`,
      raw,
    );
  }
  return {
    id,
    requestId,
    projectId,
    kind,
    input: contentFrom(record.input) ?? "",
    status,
    createdAt: stringOrNumber(record.created_at),
    completedAt: stringOrNumber(record.completed_at),
    attachments: collectionFrom(record.attachments ?? [], [
      "attachments",
      "items",
      "files",
    ]).map((item) => normalizeAttachment(item, "workspace")),
    artifactContext:
      record.artifact_context &&
      typeof record.artifact_context === "object" &&
      !Array.isArray(record.artifact_context)
        ? (record.artifact_context as JsonRecord)
        : undefined,
    intent: normalizeProjectTurnIntent(record.intent, raw),
    primaryAgentId: stringFrom(record.primary_agent_id),
    steeredInputs: normalizeProjectTurnSteeredInputs(record.steered_inputs, raw),
    routedAgentIds: stringList(record.routed_agent_ids),
    activityIds: stringList(record.activity_ids),
    resultSummary: contentFrom(record.result_summary),
    artifactIds: stringList(record.artifact_ids),
    evidenceIds: stringList(record.evidence_ids),
    error: contentFrom(record.error),
    raw: record,
  };
}

function normalizeProjectTurnSteeredInputs(
  raw: unknown,
  response: unknown,
): ProjectTurn["steeredInputs"] {
  if (raw === undefined) return [];
  if (!Array.isArray(raw)) {
    throw contractError("项目追加指令必须是数组。", response);
  }
  return raw.map((value, index) => {
    const record = recordFrom(value);
    const followUpId = stringFrom(record.follow_up_id);
    const input = contentFrom(record.input);
    const createdAt = stringOrNumber(record.created_at);
    const deliveryStatusRaw = stringFrom(record.delivery_status) ?? "queued";
    if (
      deliveryStatusRaw !== "queued" &&
      deliveryStatusRaw !== "delivered" &&
      deliveryStatusRaw !== "rejected"
    ) {
      throw contractError(
        `项目追加指令 ${index + 1} 的投递状态无效。`,
        response,
      );
    }
    const deliveryError = contentFrom(record.delivery_error);
    if (!followUpId || !input || createdAt === undefined) {
      throw contractError(
        `项目追加指令 ${index + 1} 缺少 follow_up_id/input/created_at。`,
        response,
      );
    }
    return {
      followUpId,
      input,
      createdAt,
      deliveryStatus: deliveryStatusRaw,
      deliveryError,
    };
  });
}

function normalizeProjectTurnIntent(
  raw: unknown,
  response: unknown,
): ProjectTurnIntent | undefined {
  if (raw === undefined || raw === null) return undefined;
  const record = recordFrom(raw);
  const kind = stringFrom(record.kind);
  if (kind === "project_investigation") {
    return { kind };
  }
  if (kind === "research_plan" || kind === "controlled_command") {
    return { kind };
  }
  if (kind === "continue_research") {
    const parentRequestId = stringFrom(record.parent_request_id);
    const targetAgentId = stringFrom(record.target_agent_id);
    if (parentRequestId && targetAgentId) {
      return { kind, parentRequestId, targetAgentId };
    }
  }
  throw contractError("项目请求 intent 不符合公开连续性合同。", response);
}

function normalizeComposerCapabilities(raw: unknown): ComposerCapability[] {
  const record = recordFrom(raw);
  if (!Array.isArray(record.capabilities)) {
    throw contractError("Composer 能力响应必须包含 capabilities 数组。", raw);
  }
  return record.capabilities.map((item, index) => {
    const capability = recordFrom(item);
    const id = stringFrom(capability.id);
    const kind = stringFrom(capability.kind);
    const label = stringFrom(capability.label);
    const description = stringFrom(capability.description);
    const available = booleanFrom(capability.available);
    if (
      !id ||
      !label ||
      !description ||
      available === undefined ||
      !(
        kind === "project_investigation" ||
        kind === "research_plan" ||
        kind === "controlled_command"
      )
    ) {
      throw contractError(`Composer capability ${index + 1} 不符合合同。`, raw);
    }
    return {
      id,
      kind,
      label,
      description,
      available,
      requiresAgentSelection: booleanFrom(capability.requires_agent_selection),
      permissionRequirement: stringFrom(capability.permission_requirement),
      sideEffect: stringFrom(capability.side_effect),
    };
  });
}

export function normalizeProject(
  raw: unknown,
  fallbackId: string,
): ProjectSummary {
  const record = recordFrom(raw);
  const projectId = stringFrom(record.project_id);
  const sessionId = stringFrom(record.session_id);
  const workspaceId = stringFrom(record.workspace_id);
  const workspacePath = stringFrom(record.workspace_path);
  const name = stringFrom(record.name);
  const status = stringFrom(record.status);
  const ownerUserId = stringFrom(record.owner_user_id);
  const resumable = booleanFrom(record.resumable);
  if (
    !projectId ||
    !sessionId ||
    !workspaceId ||
    !workspacePath ||
    !name ||
    !status ||
    !ownerUserId ||
    resumable === undefined
  ) {
    throw contractError(
      `项目响应 ${fallbackId} 缺少约定字段 project_id/session_id/workspace_id/workspace_path/name/status/owner_user_id/resumable。`,
      raw,
    );
  }
  if (projectId !== sessionId) {
    throw contractError(
      `项目响应违反 project_id == session_id：${projectId} != ${sessionId}。`,
      raw,
    );
  }
  return {
    projectId,
    sessionId,
    workspaceId,
    workspacePath,
    name,
    question: stringFrom(record.question),
    status,
    ownerUserId,
    updatedAt: stringOrNumber(record.updated_at),
    resumable,
    sourceMode:
      record.source_mode === "copy" || record.source_mode === "in_place"
        ? record.source_mode
        : "managed",
    sourceLabel: stringFrom(record.source_label),
    raw: record,
  };
}

export function normalizeProjectGitStatus(raw: unknown): ProjectGitStatus {
  const record = recordFrom(raw);
  const available = booleanFrom(record.available);
  const version = record.version;
  if (version !== 1 || available === undefined) {
    throw contractError("项目 Git 状态响应缺少 version/available。", raw);
  }
  const dirty = recordFrom(record.dirty);
  const staged = booleanFrom(dirty.staged);
  const unstaged = booleanFrom(dirty.unstaged);
  const untracked = booleanFrom(dirty.untracked);
  const canSwitch = booleanFrom(record.can_switch);
  if (
    staged === undefined ||
    unstaged === undefined ||
    untracked === undefined ||
    canSwitch === undefined
  ) {
    throw contractError("项目 Git 状态响应缺少 dirty/can_switch。", raw);
  }
  const branches = stringList(record.branches);
  const currentBranch = stringFrom(record.current_branch);
  const headOid = stringFrom(record.head_oid);
  if (available && (!currentBranch || !headOid)) {
    throw contractError("可用项目 Git 状态缺少当前分支或 HEAD。", raw);
  }
  return {
    version: 1,
    available,
    currentBranch,
    branches,
    headOid,
    dirty: { staged, unstaged, untracked },
    canSwitch,
    reason: stringFrom(record.reason),
    raw: record,
  };
}

function normalizeTurns(
  candidates: unknown[],
  activeTurnId?: string,
): TurnRecord[] {
  const messageLike = candidates.some((item) => {
    const role = stringFrom(recordFrom(item).role);
    return role === "user" || role === "assistant";
  });
  if (!messageLike) {
    return normalizeActiveTurnStatuses(
      candidates.map((item, index) => normalizeTurn(item, `turn-${index + 1}`)),
      activeTurnId,
    );
  }

  const turns: TurnRecord[] = [];
  for (const candidate of candidates) {
    const message = recordFrom(candidate);
    const role = stringFrom(message.role);
    const turnId =
      stringFrom(message.turn_id ?? message.id) ?? `turn-${turns.length + 1}`;
    if (role === "user") {
      turns.push({
        id: turnId,
        requestId: stringFrom(message.request_id),
        input: contentFrom(message.content ?? message.text) ?? "",
        output: "",
        status: turnId === activeTurnId ? "running" : "cancelled",
        createdAt: stringOrNumber(message.created_at ?? message.ts_ms),
        attachments: collectionFrom(message.attachments ?? [], [
          "attachments",
          "items",
        ]).map((item) => normalizeAttachment(item, "workspace")),
        raw: message,
      });
    } else if (role === "assistant") {
      const current = turns.at(-1);
      if (current && (!message.turn_id || current.id === turnId)) {
        current.output = [
          current.output,
          contentFrom(message.content ?? message.text),
        ]
          .filter(Boolean)
          .join("\n\n");
        current.status = normalizeTurnStatus(message.status) ?? "completed";
        current.error = stringFrom(message.error);
      } else {
        turns.push({
          id: turnId,
          input: "",
          output: contentFrom(message.content ?? message.text) ?? "",
          status: normalizeTurnStatus(message.status) ?? "completed",
          createdAt: stringOrNumber(message.created_at ?? message.ts_ms),
          attachments: [],
          raw: message,
        });
      }
    }
  }
  return normalizeActiveTurnStatuses(turns, activeTurnId);
}

function normalizeActiveTurnStatuses(
  turns: TurnRecord[],
  activeTurnId?: string,
): TurnRecord[] {
  return turns.map((turn) => {
    if (turn.id === activeTurnId) {
      return turn.status === "running" || turn.status === "pending"
        ? turn
        : { ...turn, status: "running" };
    }
    return turn.status === "running" || turn.status === "pending"
      ? { ...turn, status: "cancelled" }
      : turn;
  });
}

function normalizeTurn(raw: unknown, fallbackId: string): TurnRecord {
  const record = recordFrom(raw);
  const messages = collectionFrom(record.messages ?? [], ["messages", "items"]);
  const inputMessage = messages.find(
    (item) => stringFrom(recordFrom(item).role) === "user",
  );
  const outputMessages = messages.filter(
    (item) => stringFrom(recordFrom(item).role) === "assistant",
  );
  const outputFromMessages = outputMessages
    .map((item) =>
      contentFrom(recordFrom(item).content ?? recordFrom(item).text),
    )
    .filter(Boolean)
    .join("\n\n");

  return {
    id: stringFrom(record.id ?? record.turn_id) ?? fallbackId,
    requestId: stringFrom(record.request_id),
    input:
      contentFrom(
        record.input ??
          record.prompt ??
          record.user_message ??
          record.question ??
          recordFrom(inputMessage).content,
      ) ?? "",
    output:
      contentFrom(
        record.output ??
          record.answer ??
          record.report ??
          record.markdown ??
          record.assistant_message ??
          record.content,
      ) ?? outputFromMessages,
    status: normalizeTurnStatus(record.status ?? record.state) ?? "completed",
    createdAt: stringOrNumber(
      record.created_at ?? record.started_at ?? record.ts_ms,
    ),
    completedAt: stringOrNumber(record.completed_at ?? record.finished_at),
    error: stringFrom(record.error ?? record.error_message),
    attachments: collectionFrom(record.attachments ?? [], [
      "attachments",
      "items",
      "files",
    ]).map((item) => normalizeAttachment(item, "workspace")),
    raw: record,
  };
}

function serializeAttachments(attachments: AttachmentRef[]) {
  return attachments.map((attachment) => {
    if (attachment.kind !== "upload") {
      return { kind: "workspace", path: attachment.path };
    }
    const selection = attachment.documentSelection;
    return {
      kind: "upload",
      upload_id: attachment.id,
      ...(selection
        ? {
            document_selection: {
              version: selection.version,
              renderer: selection.renderer,
              upload_id: selection.uploadId,
              source_sha256: selection.sourceSha256,
              representation_sha256: selection.representationSha256,
              quote: selection.quote,
              locators: selection.locators.map((locator) => ({
                ref: locator.ref,
                kind: locator.kind,
                ...(locator.page === undefined ? {} : { page: locator.page }),
              })),
            },
          }
        : {}),
    };
  });
}

export function normalizeSession(
  raw: unknown,
  fallbackId: string,
): SessionSummary {
  const record = recordFrom(raw);
  const status = stringFrom(record.status ?? record.state);
  const id =
    stringFrom(record.id ?? record.session_id ?? record.sessionId) ??
    fallbackId;
  return {
    id,
    title:
      stringFrom(record.title ?? record.name ?? record.label) ??
      `会话 ${id.slice(0, 8)}`,
    path: stringFrom(record.path ?? record.session_path ?? record.rollout_path),
    status,
    updatedAt: stringOrNumber(
      record.updated_at ?? record.modified_at ?? record.ts_ms,
    ),
    running:
      booleanFrom(record.running ?? record.is_running) ??
      ["running", "active", "streaming"].includes(status?.toLowerCase() ?? ""),
    resumable:
      booleanFrom(record.resumable ?? record.can_resume) ??
      Boolean(record.path ?? record.session_path ?? record.rollout_path),
    raw: record,
  };
}

export function normalizeAttachment(
  raw: unknown,
  fallbackKind: AttachmentRef["kind"],
  fallbackName?: string,
): AttachmentRef {
  const record = recordFrom(raw);
  const path = stringFrom(record.path ?? record.upload_path);
  return {
    id: stringFrom(record.id ?? record.attachment_id ?? record.upload_id),
    name:
      stringFrom(
        record.name ??
          record.filename ??
          record.original_filename ??
          record.label,
      ) ??
      path?.split("/").filter(Boolean).at(-1) ??
      fallbackName ??
      "附件",
    path,
    projectId: stringFrom(record.project_id),
    sessionId: stringFrom(record.session_id),
    workspacePath: stringFrom(record.workspace_path),
    sha256: stringFrom(record.sha256),
    kind:
      stringFrom(record.kind ?? record.source) === "upload" ||
      fallbackKind === "upload"
        ? "upload"
        : "workspace",
    mimeType: stringFrom(record.mime_type ?? record.content_type),
    size: numberFrom(record.size ?? record.size_bytes ?? record.bytes),
    inspection: normalizeAttachmentInspection(record.inspection),
    documentSelection: normalizeUploadDocumentSelection(record.document_selection),
    raw: record,
  };
}

function normalizeUploadDocumentSelection(
  raw: unknown,
): AttachmentRef["documentSelection"] {
  const record = recordFrom(raw);
  if (
    record.version !== 1 ||
    record.renderer !== "docling" ||
    typeof record.upload_id !== "string" ||
    typeof record.source_sha256 !== "string" ||
    typeof record.representation_sha256 !== "string" ||
    typeof record.quote !== "string" ||
    !Array.isArray(record.locators)
  ) {
    return undefined;
  }
  const locators = record.locators.flatMap<UploadDocumentSelectionLocator>(
    (value) => {
    const locator = recordFrom(value);
    const kind = locator.kind;
    if (
      typeof locator.ref !== "string" ||
      (kind !== "text" && kind !== "table" && kind !== "formula") ||
      (locator.page !== undefined &&
        (!Number.isInteger(locator.page) || (locator.page as number) < 1))
    ) {
      return [];
    }
    return [
      {
        ref: locator.ref,
        kind,
        ...(typeof locator.page === "number" ? { page: locator.page } : {}),
      },
    ];
    },
  );
  return locators.length === record.locators.length && locators.length
    ? {
        version: 1,
        renderer: "docling",
        uploadId: record.upload_id,
        sourceSha256: record.source_sha256,
        representationSha256: record.representation_sha256,
        quote: record.quote,
        locators,
      }
    : undefined;
}

function normalizeUploadDocumentRepresentation(
  raw: unknown,
  fallbackUploadId: string,
): UploadDocumentRepresentation {
  const record = recordFrom(raw);
  const document = recordFrom(record.document);
  const uploadId = stringFrom(record.upload_id) ?? fallbackUploadId;
  const mimeType = stringFrom(record.mime_type);
  const sourceSha256 = stringFrom(record.source_sha256);
  if (
    record.version !== 1 ||
    !mimeType ||
    !sourceSha256 ||
    !Object.keys(document).length
  ) {
    throw contractError(
      "上传文档视图响应缺少 version/mime_type/source_sha256/document。",
      raw,
    );
  }
  return {
    version: 1,
    uploadId,
    originalFilename: stringFrom(record.original_filename),
    mimeType,
    sourceSha256,
    document,
    raw: record,
  };
}

function normalizeAttachmentInspection(
  raw: unknown,
): AttachmentInspection | undefined {
  const record = recordFrom(raw);
  const status = stringFrom(record.status) as
    | AttachmentInspectionStatus
    | undefined;
  if (
    !status ||
    ![
      "stored",
      "derived_available",
      "unsupported",
      "unavailable",
      "failed",
      "integrity_mismatch",
    ].includes(status)
  ) {
    return undefined;
  }
  const reason = recordFrom(record.reason);
  const reasonCode = stringFrom(reason.code);
  const reasonMessage = stringFrom(reason.message);
  return {
    version: numberFrom(record.version),
    status,
    kind: stringFrom(record.kind),
    sourceSha256: stringFrom(record.source_sha256),
    metadataRuntimeRelativePath: stringFrom(
      record.metadata_runtime_relative_path,
    ),
    contentRuntimeRelativePath: stringFrom(
      record.content_runtime_relative_path,
    ),
    executionRuntimeRelativePath: stringFrom(
      record.execution_runtime_relative_path,
    ),
    checksumRuntimeRelativePath: stringFrom(
      record.checksum_runtime_relative_path,
    ),
    parser:
      record.parser && typeof record.parser === "object" && !Array.isArray(record.parser)
        ? (record.parser as JsonRecord)
        : undefined,
    coverage:
      record.coverage &&
      typeof record.coverage === "object" &&
      !Array.isArray(record.coverage)
        ? (record.coverage as JsonRecord)
        : undefined,
    reason:
      reasonCode && reasonMessage
        ? { code: reasonCode, message: reasonMessage }
        : undefined,
    raw: record,
  };
}

function validateUploadScope(
  attachment: AttachmentRef,
  expectedSessionId: string,
  raw: unknown,
) {
  if (
    !attachment.id ||
    !attachment.projectId ||
    !attachment.sessionId ||
    !attachment.mimeType ||
    !attachment.sha256
  ) {
    throw contractError(
      "上传响应缺少 upload_id/project_id/session_id/mime_type/sha256。",
      raw,
    );
  }
  if (
    attachment.projectId !== expectedSessionId ||
    attachment.sessionId !== expectedSessionId ||
    attachment.projectId !== attachment.sessionId
  ) {
    throw contractError(
      `上传响应作用域不匹配：expected=${expectedSessionId}, project_id=${attachment.projectId}, session_id=${attachment.sessionId}。`,
      raw,
    );
  }
}

function contractError(message: string, body: unknown) {
  return new ApiError(message, 502, body);
}

export function normalizeEvidence(
  raw: unknown,
  fallbackId: string,
): EvidenceRecord {
  const record = recordFrom(raw);
  const annotation = recordFrom(record.annotation);
  const confidence = recordFrom(record.confidence);
  const verified = booleanFrom(record.scientific_verified ?? record.verified);
  const quarantined = booleanFrom(record.quarantined ?? record.is_quarantined);
  const status = quarantined
    ? "quarantined"
    : normalizeEvidenceStatus(
        record.status ??
          record.verification_status ??
          (verified === true ? "verified" : undefined),
      );
  const origin =
    booleanFrom(annotation.demo) === true
      ? "demo"
      : booleanFrom(annotation.external_network_used) === true
        ? "network"
        : booleanFrom(annotation.local_first) === true
          ? "local"
          : normalizeEvidenceOrigin(
              record.origin ?? record.source_type ?? record.provenance,
            );
  const source = stringFrom(
    record.source ?? record.url ?? record.path ?? record.citation,
  );
  return {
    id: stringFrom(record.id ?? record.evidence_id) ?? fallbackId,
    title:
      stringFrom(
        record.title ?? record.name ?? record.label ?? annotation.title,
      ) ??
      source ??
      `证据 ${fallbackId.replace("evidence-", "")}`,
    status,
    origin,
    confidence: stringOrNumber(
      record.confidence_score ??
        confidence.score ??
        confidence.level ??
        record.confidence,
    ),
    confidenceExplanation: stringFrom(
      record.confidence_explanation ??
        record.confidence_reason ??
        record.confidence_note ??
        confidence.reason,
    ),
    summary: contentFrom(
      record.summary ??
        record.description ??
        record.claim ??
        annotation.summary,
    ),
    warnings: stringList(record.warnings ?? record.warning),
    notes: stringList(record.notes ?? record.note),
    source,
    raw: record,
  };
}

export function normalizeArtifact(
  raw: unknown,
  fallbackId: string,
): ArtifactRecord {
  const record = recordFrom(raw);
  const path = stringFrom(record.path ?? record.relative_path);
  const id = stringFrom(record.id ?? record.artifact_id) ?? fallbackId;
  const provenance =
    record.provenance &&
    typeof record.provenance === "object" &&
    !Array.isArray(record.provenance)
      ? (record.provenance as JsonRecord)
      : undefined;
  const provenanceSummary = normalizeArtifactProvenanceSummary(
    record.provenance_summary ?? record.provenanceSummary,
  );
  return {
    id,
    name:
      stringFrom(record.name ?? record.filename ?? record.title) ??
      path?.split("/").filter(Boolean).at(-1) ??
      id,
    path,
    kind: stringFrom(record.kind ?? record.type),
    mimeType: stringFrom(record.mime_type ?? record.content_type),
    size: numberFrom(record.size ?? record.size_bytes ?? record.bytes),
    status: stringFrom(record.status),
    ownerAgentId: stringFrom(
      record.owner_agent_id ?? record.ownerAgentId ?? record.owner,
    ),
    executionId: stringFrom(record.execution_id ?? record.executionId),
    provenance,
    provenanceSummary,
    contentSha256: stringFrom(record.content_sha256 ?? record.contentSha256),
    coordinateSha256: stringFrom(
      record.coordinate_sha256 ?? record.coordinateSha256,
    ),
    previewUrl: safeResponseUrl(stringFrom(record.preview_url)),
    downloadUrl: safeResponseUrl(stringFrom(record.download_url)),
    raw: record,
  };
}

function normalizeArtifactProvenanceSummary(
  raw: unknown,
): ArtifactRecord["provenanceSummary"] | undefined {
  const value = recordFrom(raw);
  const source = stringFrom(value.source);
  const recordType = stringFrom(value.record_type ?? value.recordType);
  const tool = stringFrom(value.tool);
  return source || recordType || tool ? { source, recordType, tool } : undefined;
}

export function isListedArtifact(_artifact: ArtifactRecord): boolean {
  // The Artifact API contains only Project-scoped records that passed server
  // admission. Keeping a second review policy in the browser hides real work.
  return true;
}

export function normalizeOrganizationObservation(
  raw: unknown,
): OrganizationObservation {
  const record = recordFrom(raw);
  const agents = collectionFrom(record.agents, ["agents", "items", "data"]).map(
    (item, index) => {
      const agent = recordFrom(item);
      return {
        id: stringFrom(agent.id ?? agent.agent_id) ?? `agent-${index + 1}`,
        name:
          stringFrom(agent.name) ??
          stringFrom(agent.id ?? agent.agent_id) ??
          `Agent ${index + 1}`,
        role: stringFrom(agent.role) ?? "Agent",
        threadId: stringFrom(agent.thread_id ?? agent.threadId),
        canonicalWorkspace: stringFrom(
          agent.canonical_workspace ?? agent.workspace,
        ),
        liveWorker: booleanFrom(agent.live_worker ?? agent.liveWorker) ?? false,
        btwAvailable:
          booleanFrom(agent.btw_available ?? agent.btwAvailable) ?? false,
        status: stringFrom(agent.status),
        recentSummaries: stringList(
          agent.recent_summaries ?? agent.recentSummaries,
        ),
        statusError: stringFrom(agent.status_error ?? agent.statusError),
        raw: agent,
      } satisfies OrganizationAgentObservation;
    },
  );
  return {
    version: numberFrom(record.version) ?? 1,
    enabled: booleanFrom(record.enabled) ?? false,
    canonicalOrganizationPath: stringFrom(record.canonical_organization_path),
    acceptingInput: booleanFrom(record.accepting_input) ?? false,
    lastActivationError: stringFrom(record.last_activation_error),
    pendingReplyTargets:
      record.pending_reply_targets === undefined
        ? undefined
        : collectionFrom(record.pending_reply_targets, ["items", "data"]).flatMap(
            (item) => {
              const target = recordFrom(item);
              const participantId = stringFrom(
                target.participant_id ?? target.participantId,
              );
              const messageId = stringFrom(target.message_id ?? target.messageId);
              return participantId && messageId
                ? [{ participantId, messageId }]
                : [];
            },
          ),
    agents,
    raw: record,
  };
}

export function normalizeOrganizationBTWResponse(
  raw: unknown,
): OrganizationBTWResponse {
  const record = recordFrom(raw);
  const answer = recordFrom(record.answer);
  const version = numberFrom(record.version);
  const requestId = stringFrom(record.request_id);
  const agentId = stringFrom(record.agent_id);
  const status = stringFrom(record.status);
  const text = stringFrom(answer.text);
  const source = stringFrom(answer.source);
  if (
    version !== 1 ||
    !requestId ||
    !agentId ||
    status !== "completed" ||
    !text ||
    source !== "agent"
  ) {
    throw contractError(
      "BTW 响应必须包含 version=1、request_id、agent_id、status=completed 以及 answer.text/source=agent。",
      raw,
    );
  }
  return {
    version: 1,
    request_id: requestId,
    agent_id: agentId,
    status: "completed",
    answer: { text, source: "agent" },
  };
}

export function normalizeOrganizationStatus(
  raw: unknown,
  fallbackAgentId: string,
): OrganizationStatus {
  const record = recordFrom(raw);
  return {
    version: numberFrom(record.version) ?? 1,
    agentId: stringFrom(record.agent_id ?? record.agentId) ?? fallbackAgentId,
    status: stringFrom(record.status) ?? "unknown",
    recentSummaries: stringList(
      record.recent_summaries ?? record.recentSummaries,
    ),
    statusError: stringFrom(record.status_error ?? record.statusError),
    raw: record,
  };
}

export function normalizePermission(
  raw: unknown,
  fallbackId: string,
): PermissionRequest {
  const record = recordFrom(raw);
  return {
    requestId: stringFrom(record.request_id ?? record.id) ?? fallbackId,
    target:
      stringFrom(
        record.target ?? record.command ?? record.tool ?? record.action,
      ) ?? "受限操作",
    reason:
      stringFrom(record.reason ?? record.description ?? record.message) ??
      "需要人工确认。",
    status: stringFrom(record.status ?? record.state),
    raw: record,
  };
}

function normalizeWorkspaceListing(
  raw: unknown,
  requestedPath: string,
): WorkspaceListing {
  const record = recordFrom(raw);
  const entries = collectionFrom(raw, [
    "entries",
    "files",
    "items",
    "children",
    "data",
  ]).map((item, index) =>
    normalizeWorkspaceEntry(item, `${requestedPath}/entry-${index + 1}`),
  );
  return {
    path: stringFrom(record.path ?? record.current_path) ?? requestedPath,
    parent: stringFrom(record.parent ?? record.parent_path),
    entries,
  };
}

function normalizeWorkspaceEntry(
  raw: unknown,
  fallbackPath: string,
): WorkspaceEntry {
  const record = recordFrom(raw);
  const path = stringFrom(record.path ?? record.relative_path) ?? fallbackPath;
  const rawKind = stringFrom(record.kind ?? record.type)?.toLowerCase();
  const isDirectory =
    booleanFrom(record.is_dir ?? record.directory) ??
    (rawKind === "directory" || rawKind === "dir" || rawKind === "folder");
  return {
    name:
      stringFrom(record.name ?? record.filename ?? record.label) ??
      path.split("/").filter(Boolean).at(-1) ??
      path,
    path,
    kind: isDirectory ? "directory" : "file",
    size: numberFrom(record.size ?? record.size_bytes ?? record.bytes),
    mimeType: stringFrom(record.mime_type ?? record.content_type),
    raw: record,
  };
}

function normalizeTurnStatus(value: unknown): TurnStatus | undefined {
  const status = stringFrom(value)?.toLowerCase();
  if (!status) return undefined;
  if (["pending", "queued"].includes(status)) return "pending";
  if (["running", "active", "streaming", "started"].includes(status))
    return "running";
  if (status === "interrupted_recoverable") return "interrupted_recoverable";
  if (status === "result_unknown") return "result_unknown";
  if (
    ["completed", "complete", "done", "success", "succeeded"].includes(status)
  ) {
    return "completed";
  }
  if (["error", "failed", "failure"].includes(status)) return "error";
  if (["cancelled", "canceled", "stopped"].includes(status)) return "cancelled";
  return undefined;
}

function normalizeEvidenceStatus(value: unknown): EvidenceStatus {
  const status = stringFrom(value)?.toLowerCase();
  if (status?.includes("quarant")) return "quarantined";
  if (status?.includes("unverified") || status?.includes("pending"))
    return "unverified";
  if (status?.includes("verified") || status?.includes("valid"))
    return "verified";
  return "unverified";
}

function normalizeEvidenceOrigin(value: unknown): EvidenceOrigin {
  const origin = stringFrom(value)?.toLowerCase();
  if (!origin) return "unknown";
  if (
    origin.includes("network") ||
    origin.includes("external") ||
    origin.includes("web")
  ) {
    return "network";
  }
  if (
    origin.includes("demo") ||
    origin.includes("mock") ||
    origin.includes("sample")
  )
    return "demo";
  if (
    origin.includes("local") ||
    origin.includes("workspace") ||
    origin.includes("file")
  ) {
    return "local";
  }
  return "unknown";
}

export function recordFrom(value: unknown): JsonRecord {
  if (value && typeof value === "object" && !Array.isArray(value))
    return value as JsonRecord;
  return {};
}

export function collectionFrom(value: unknown, keys: string[]): unknown[] {
  if (Array.isArray(value)) return value;
  const record = recordFrom(value);
  for (const key of keys) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }
  if (Object.keys(record).length && keys.some((key) => key in record))
    return [];
  return Object.keys(record).length ? [record] : [];
}

export function stringFrom(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim()) return value;
  return undefined;
}

export function numberFrom(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (
    typeof value === "string" &&
    value.trim() &&
    Number.isFinite(Number(value))
  ) {
    return Number(value);
  }
  return undefined;
}

function booleanFrom(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (value === "true" || value === 1 || value === "1") return true;
  if (value === "false" || value === 0 || value === "0") return false;
  return undefined;
}

function stringOrNumber(value: unknown): string | number | undefined {
  return stringFrom(value) ?? numberFrom(value);
}

export function contentFrom(value: unknown): string | undefined {
  const direct = stringFrom(value);
  if (direct) return direct;
  if (Array.isArray(value)) {
    const content = value
      .map((part) => {
        const record = recordFrom(part);
        return stringFrom(record.text ?? record.content ?? record.markdown);
      })
      .filter(Boolean)
      .join("\n");
    return content || undefined;
  }
  const record = recordFrom(value);
  return stringFrom(
    record.markdown ?? record.text ?? record.content ?? record.value,
  );
}

export function stringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      const text = stringFrom(item) ?? contentFrom(item);
      return text ? [text] : [];
    });
  }
  const text = stringFrom(value) ?? contentFrom(value);
  return text ? [text] : [];
}

function safeResponseUrl(value?: string): string | undefined {
  if (!value) return undefined;
  if (
    value.startsWith("/api/") ||
    value.startsWith("api/") ||
    value.startsWith("https://") ||
    value.startsWith("http://")
  ) {
    return value;
  }
  return undefined;
}
